import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { Spin } from "antd";
import { observer } from "mobx-react-lite";
import dayjs from "dayjs";
import { EasyAvatar } from "fin-web-components";
import { ViewpointReportApi } from "../../products/viewpoint/ViewpointReportApi";
import type {
  ViewpointReportRow,
} from "../../products/viewpoint/ViewpointReportTypes";
import { parseReportTargetSnapshot } from "../../products/viewpoint/ViewpointReportTypes";
import { resolveAppMediaUrl } from "../../utils/resolveAppMediaUrl";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { formatApiDateTime } from "../../utils/Backend";
import "../AccountPage.css";
import "./MyReportsPanel.css";

function displayTime(raw: unknown): string {
  const t = formatApiDateTime(raw);
  if (!t) return "—";
  const d = dayjs(t);
  return d.isValid() ? d.format("YYYY-MM-DD HH:mm") : t;
}

function ReportCard({ row }: { row: ViewpointReportRow }): JSX.Element {
  const snap = parseReportTargetSnapshot(row.reportTargetContent);
  const nick =
    snap?.publisherNickname?.trim() ||
    snap?.publisherLabel?.trim() ||
    "用户";
  const content = snap?.content?.trim() || "（内容已不可见）";
  return (
    <article className="lf-my-report-card">
      <div className="lf-my-report-card__head">
        <span className="lf-my-report-card__type">{row.reportTypeLabel}</span>
        <span
          className={
            row.status === "completed"
              ? "lf-my-report-card__status lf-my-report-card__status--done"
              : "lf-my-report-card__status"
          }
        >
          {row.statusLabel}
        </span>
      </div>
      <div className="lf-my-report-card__target">
        <EasyAvatar
          shape="square"
          squareRadius={4}
          size={28}
          url={resolveAppMediaUrl(snap?.publisherAvatarUrl) || undefined}
          word={avatarDisplayWord(nick, snap?.publisherLabel)}
          alt=""
        />
        <div className="lf-my-report-card__target-main">
          <div className="lf-my-report-card__nick">{nick}</div>
          <div className="lf-my-report-card__content">{content}</div>
        </div>
      </div>
      {row.detailNote?.trim() ? (
        <div className="lf-my-report-card__note">说明：{row.detailNote.trim()}</div>
      ) : null}
      <div className="lf-my-report-card__meta">提交于 {displayTime(row.createdAt)}</div>
      {row.status === "completed" && row.handleResult?.trim() ? (
        <div className="lf-my-report-card__result">
          处理结果：{row.handleResult.trim()}
        </div>
      ) : null}
    </article>
  );
}

export function MyReportsPanel(): JSX.Element {
  const [rows, setRows] = useState<ViewpointReportRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ViewpointReportApi.listMine({ pageNum: 1, pageSize: 50 });
      if (typeof res === "string") {
        setRows([]);
        return;
      }
      setRows(Array.isArray(res.list) ? res.list : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Spin spinning={loading}>
      {rows.length === 0 && !loading ? (
        <div className="lf-my-report-empty">暂无举报记录</div>
      ) : (
        <div className="lf-my-report-list">
          {rows.map((r) => <ReportCard key={r.id} row={r} />)}
        </div>
      )}
    </Spin>
  );
}

type PageProps = {
  topBar: ReactNode;
  fixedTopBar?: boolean;
};

function MyReportsPage(props: PageProps): JSX.Element {
  const { topBar, fixedTopBar = false } = props;
  const mainClass = fixedTopBar
    ? "customer-account-page__main customer-account-page__main--below-fixed-top"
    : "customer-account-page__main";
  return (
    <div className="customer-account-page">
      {topBar}
      <main className={mainClass}>
        <div className="customer-account-page__inner">
          <div className="customer-account-page__card">
            <h1 className="customer-account-page__title">我的举报</h1>
            <MyReportsPanel />
          </div>
        </div>
      </main>
    </div>
  );
}

export default observer(MyReportsPage);
